import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from "recharts";
import api from "../lib/api";
import { Money, usePrivacy } from "../context/PrivacyContext";
import { fmtEUR } from "../lib/format";

const SCENARIOS = [
  { key: "conservador", label: "Conservador", color: "#EF4444", desc: "Ingresos −10 %, gastos +10 %" },
  { key: "realista", label: "Realista", color: "#0A0A0A", desc: "Según tus datos actuales" },
  { key: "optimista", label: "Optimista", color: "#10B981", desc: "Ingresos +5 %, gastos −5 %" },
];

export default function Proyecciones() {
  const [data, setData] = useState(null);
  const { hidden } = usePrivacy();

  useEffect(() => {
    api.get("/projections").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-sm text-muted-foreground">Calculando proyecciones…</div>;

  const chartData = data.escenarios.realista.serie.map((p, i) => ({
    mes: p.mes,
    conservador: data.escenarios.conservador.serie[i].saldo,
    realista: p.saldo,
    optimista: data.escenarios.optimista.serie[i].saldo,
  }));

  return (
    <div className="space-y-6" data-testid="proyecciones-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tighter">Proyecciones</h1>
        <p className="text-sm text-muted-foreground mt-1">¿Dónde estarás dentro de 1 mes, 3 meses o 1 año?</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="border border-border rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Saldo actual</div>
          <Money value={data.saldo_actual} testId="proj-saldo-actual" className="block mt-1 text-xl font-semibold tabular" />
        </div>
        <div className="border border-border rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Ingresos / mes</div>
          <Money value={data.ingresos_mensuales} className="block mt-1 text-xl font-semibold tabular text-emerald-600" />
        </div>
        <div className="border border-border rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Gastos fijos / mes</div>
          <Money value={data.gastos_fijos} className="block mt-1 text-xl font-semibold tabular" />
        </div>
        <div className="border border-border rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Variables (media)</div>
          <Money value={data.gastos_variables_media} className="block mt-1 text-xl font-semibold tabular" />
        </div>
      </div>

      <div className="border border-border rounded-lg p-5">
        <h3 className="font-heading font-bold text-lg mb-4">Evolución a 12 meses</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
              <XAxis dataKey="mes" tick={{ fontSize: 10, fill: "#8F8F94" }} tickLine={false} axisLine={false} interval={1} />
              <YAxis tick={{ fontSize: 10, fill: "#8F8F94" }} tickLine={false} axisLine={false} tickFormatter={(v) => (hidden ? "•••" : `${Math.round(v / 1000)}k`)} width={40} />
              <Tooltip formatter={(v) => (hidden ? "•••••• €" : fmtEUR(v))} contentStyle={{ borderRadius: 8, border: "1px solid #E5E5EA", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <ReferenceLine y={0} stroke="#EF4444" strokeDasharray="4 4" />
              {SCENARIOS.map((s) => (
                <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color} strokeWidth={s.key === "realista" ? 2.5 : 1.5} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {SCENARIOS.map((s) => (
          <div key={s.key} className="border border-border rounded-lg p-5" data-testid={`scenario-${s.key}`}>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
              <h3 className="font-heading font-bold">{s.label}</h3>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 mb-4">{s.desc}</p>
            <div className="space-y-2">
              {[["30 días", "30"], ["90 días", "90"], ["180 días", "180"], ["365 días", "365"]].map(([l, k]) => {
                const v = data.escenarios[s.key].hitos[k];
                return (
                  <div key={k} className="flex items-center justify-between py-1 border-b border-border last:border-0">
                    <span className="text-sm text-muted-foreground">{l}</span>
                    <Money value={v} className={`text-sm font-semibold tabular ${v < 0 ? "text-red-600" : ""}`} />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
